import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Trophy, Users, TrendingUp, Ticket, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { authFetch } from '@/lib/auth-client';

interface Stats {
  totalMainEvents: number;
  activeMainEvents: number;
  totalUsers: number;
  totalEntries: number;
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStats() {
      try {
        setIsLoading(true);
        setError(null);

        const response = await authFetch('/api/admin/stats');

        if (!response.ok) {
          throw new Error('Failed to fetch stats');
        }

        const data = await response.json();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch admin stats:', err);
        setError('Failed to load dashboard stats');
      } finally {
        setIsLoading(false);
      }
    }

    fetchStats();
  }, []);

  const cards = [
    {
      title: 'Main Events',
      value: stats?.totalMainEvents || 0,
      icon: Trophy,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      href: '/admin/main-events',
    },
    {
      title: 'Active Events',
      value: stats?.activeMainEvents || 0,
      icon: TrendingUp,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      href: '/admin/main-events',
    },
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      href: '/admin/users',
    },
    {
      title: 'Total Entries',
      value: stats?.totalEntries || 0,
      icon: Ticket,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      href: '/admin/main-events',
    },
  ];

  return (
    <div>
      <h1 className="text-xl sm:text-2xl font-bold text-white mb-5 sm:mb-6">Admin Dashboard</h1>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.title}
              href={card.href}
              className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`h-10 w-10 rounded-lg ${card.bgColor} flex items-center justify-center`}>
                  <Icon className={`h-5 w-5 ${card.color}`} />
                </div>
                <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
              </div>
              {isLoading ? (
                <Loader2 className="h-6 w-6 text-zinc-500 animate-spin" />
              ) : (
                <p className="text-2xl font-bold text-white">{card.value}</p>
              )}
              <p className="text-sm text-zinc-400">{card.title}</p>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="rounded-xl bg-zinc-900 border border-zinc-800 p-4">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link
            href="/admin/main-events/new"
            className="px-4 py-2 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors text-sm font-medium"
          >
            Create Main Event
          </Link>
          <Link
            href="/admin/main-events"
            className="px-4 py-2 rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors text-sm font-medium"
          >
            Manage Main Events
          </Link>
        </div>
      </div>
    </div>
  );
}
